import { on } from '@ember/modifier';
import { action } from '@ember/object';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import { didInsert, didUpdate, willDestroy } from '@ember/render-modifiers';
import { type Registry as Services, service } from '@ember/service';
import { isPresent } from '@ember/utils';
import { isTesting, macroCondition } from '@embroider/macros';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismLoadingBar, PrismMessage } from '@mylighthouse/prism-ember';
import { task, timeout } from 'ember-concurrency';
import { t } from 'ember-intl';
import ENV from 'lumen/config/environment';

const DEFAULT_DURATION_IN_MILLISECONDS = 30000;

const STAFF_ONLY_QUERY_PARAMS = ['data_replatforming', 'debug'];

const IFRAME_STATE = {
  LOADING: 'loading',
  TIMEOUT: 'timeout',
  LOADED: 'loaded',
};

// TODO @vfuto: add to ENV
const TRUSTED_ORIGINS = [ENV.lookerEmbedURL];

interface GeneralEmbeddedAppSignature {
  Args: {
    baseURL: string;
    dataTestSkipURLMock?: boolean;
    id: string;
    loadingTimeout: number;
    onMessage?: (type: string, dashboard: string) => void;
    preventReload?: boolean;
    query?: Record<string, unknown>;
    url: string;
  };
  Element: HTMLIFrameElement;
}

export default class GeneralEmbeddedApp extends Component<GeneralEmbeddedAppSignature> {
  @service declare access: Services['access'];
  @service declare currency: Services['currency'];
  @service declare currencyConversion: Services['currency-conversion'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service('looker-embed-session')
  declare lookerEmbedSession: Services['looker-embed-session'];
  @service declare router: Services['router'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked defaultUrl;
  @tracked state = IFRAME_STATE.LOADING;

  // @ts-expect-error: Incorrect type
  element;

  query;

  loadingIframeTask = task({ restartable: true }, async () => {
    this.state = IFRAME_STATE.LOADING;

    await timeout(this.args.loadingTimeout ?? DEFAULT_DURATION_IN_MILLISECONDS);

    this.state = IFRAME_STATE.TIMEOUT;
  });

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isLoaded() {
    return this.state === IFRAME_STATE.LOADED;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isLoading() {
    return this.state === IFRAME_STATE.LOADING;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isTimedout() {
    return this.state === IFRAME_STATE.TIMEOUT;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get url() {
    /**
     * This mocking was originally done using `owner.register`, but since embroider,
     * mocking shouldn't be done using this approach any more, hence the test condition
     */
    if (macroCondition(isTesting())) {
      if (!this.args.dataTestSkipURLMock) {
        /**
         * we need to make it somewhat dynamic, so if the URL changes
         * iframe won't stuck in loading state
         */
        return `data:text/html;charset=utf-8,%3Chtml%3E%3Cbody%3EHola Mundo amigo ${this.defaultUrl}%3C/body%3E%3C/html%3E`;
      }
    }

    const url = new URL(
      this.defaultUrl,
      this.args.baseURL ?? window.location.href,
    );

    if (isPresent(this.query)) {
      // @ts-expect-error: Incorrect type
      const searchParams = new URLSearchParams(this.query);
      // @ts-expect-error: Incorrect type
      url.search = searchParams;
    }

    return url.toString();
  }

  constructor() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line prefer-rest-params
    super(...arguments);

    // prevent iframe reload if QP changes
    this.query = this.sanitizeQuery(this.args.query);

    // setting the arg url to a local tracked property,
    // did-update modifier will update this if the arg url changes
    // and starts the loading task (if not prevented)
    this.defaultUrl = this.args.url;
  }

  sanitizeQuery(
    query?: Record<string, unknown>,
  ): Record<string, unknown> | undefined {
    if (this.access.isStaffUser || !query) {
      return query;
    }

    return Object.fromEntries(
      Object.entries(query).filter(
        ([key]) => !STAFF_ONLY_QUERY_PARAMS.includes(key),
      ),
    );
  }

  @action
  onDestroy(): void {
    // eslint-disable-next-line @typescript-eslint/unbound-method
    window.removeEventListener('message', this.onMessage);
  }

  @action
  onInsert(): void {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.loadingIframeTask.perform();
  }

  @action
  onLoad(): void {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.loadingIframeTask.cancelAll();
    this.state = IFRAME_STATE.LOADED;
  }

  /**
   * We recieve a message from a managed iframe, this handler will be invoked
   */
  @action
  // @ts-expect-error: Incorrect type
  onMessage({ data, origin }): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const message = typeof data === 'string' ? JSON.parse(data) : data;

    if (macroCondition(!isTesting())) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      if (origin !== this.args.baseURL && !TRUSTED_ORIGINS.includes(origin)) {
        return;
      }
    }

    // Spider iframe messages
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    switch (message.event) {
      // Sent by Spider iframe for redirecting `/spider-hotel/:spider_hotel_id/:module`
      case 'force-redirect':
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        window.location = message.url;
        break;
      case 'navigate':
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
        this.router.transitionTo(message.route, message.model);
        break;
      case 'replace':
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
        this.router.replaceWith(message.route, message.model);
        break;
      case 'update':
        this.router.replaceWith({
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          queryParams: message.queryParams,
        });
        break;
      case 'spider-navigate': {
        // @ts-expect-error: Incorrect type
        const { name, queryParams } = this.router.currentRoute;
        const { hotelOrMarket } = this.hotelAndMarketService;

        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
        this.router.transitionTo(name, hotelOrMarket.id, message.dashboard, {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          queryParams: { ...queryParams, ...message.queryParams },
        });
        break;
      }
      case 'spider-replace': {
        // @ts-expect-error: Incorrect type
        const { name, queryParams } = this.router.currentRoute;
        const { hotelOrMarket } = this.hotelAndMarketService;

        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
        this.router.replaceWith(name, hotelOrMarket.id, message.dashboard, {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          queryParams: { ...queryParams, ...message.queryParams },
        });
        break;
      }
      case 'spider-update': {
        // @ts-expect-error: Incorrect type
        const { queryParams } = this.router.currentRoute;

        this.router.replaceWith({
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          queryParams: { ...queryParams, ...message.queryParams },
        });
        break;
      }
      case 'spider-track-event': {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
        this.usageAnalytics.track(message.name, {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          eventTrigger: message.trigger,
          product: 'business-intelligence',
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          ...message.data,
        });
        break;
      }
      case 'spider-currency-changed':
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
        this.currency.setCurrency(message.currency);
        break;
      case 'spider-currency-profile-changed':
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
        this.currencyConversion.setSelectedProfileId(message.profileId);
        break;
      default:
        break;
    }

    // Looker iframe messages
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    switch (message.type) {
      case 'dashboard:loaded':
      case 'dashboard:edit:cancel':
      case 'dashboard:edit:start':
      case 'dashboard:save:complete':
      case 'dashboard:run:complete':
      case 'dashboard:filters:changed':
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
        this.args.onMessage?.(message.type, message.dashboard);
        break;
      case 'session:tokens:request':
        // eslint-disable-next-line @typescript-eslint/no-floating-promises, @typescript-eslint/no-unsafe-argument
        this.lookerEmbedSession.respondToTokensRequest(this.element);
        break;
      default:
        break;
    }
  }

  @action
  onRetry(): void {
    if (!this.args.preventReload) {
      this.query = this.sanitizeQuery(this.args.query);
      this.defaultUrl = this.args.url;
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.loadingIframeTask.perform();
    }
  }

  @action
  // @ts-expect-error: Incorrect type
  registerHandlers(iframeElement): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    this.element = iframeElement;
    // eslint-disable-next-line @typescript-eslint/unbound-method
    window.addEventListener('message', this.onMessage);
  }

  <template>
    {{! template-lint-disable require-iframe-title }}
    {{#if this.isLoading}}
      <div
        class="loading-bar-container"
        data-test-id="embedded-app-loading-state"
      >
        <PrismLoadingBar />
      </div>
    {{/if}}

    <div
      class="iframe-container h-full"
      {{didInsert this.onInsert}}
      {{didUpdate this.onRetry @url}}
      {{willDestroy this.onDestroy}}
    >
      {{#if this.isTimedout}}
        <div
          class="flex h-full w-full items-center justify-center"
          data-test-id="embedded-app-timeout-error"
        >
          <PrismMessage
            @description={{t "general.error"}}
            @illustration="100x100_critical"
            @title={{t "general.error_title"}}
            data-test-id="embedded-app-empty-state"
          />
        </div>
      {{else}}
        <iframe
          class="h-full w-full {{unless this.isLoaded 'invisible'}}"
          data-test-id="embedded-app-iframe"
          id={{@id}}
          src={{this.url}}
          {{didInsert this.registerHandlers}}
          {{on "load" this.onLoad}}
          ...attributes
        ></iframe>
      {{/if}}
    </div>
  </template>
}
